import * as React from 'react';
import { Alert, Button, Chip, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import { approveBetupFeedPost, BETUP_FEED_LABELS, getBetupFeedPosts, rejectBetupFeedPost } from '../Api';
import { BetupFeedAdminPostDto, BetupFeedReviewStatus } from '../models/dtos/BetupFeedPostDto';
import {
    CategoryChips, ConfirmDialog, FeedImage, FeedPlayer, formatCount, formatFeedDate, formatUsd, LinkedEntity,
    loadErrorText, PostText, RejectPostDialog, ReviewedByChip, ReviewStatusChip, shorten,
} from '../components/betupFeed/BetupFeedShared';
import { BetupFeedReviewCategory } from '../models/dtos/BetupFeedPostDto';

const TABS: Array<{ status: BetupFeedReviewStatus; label: string; query: string }> = [
    { status: 'PENDING', label: 'Pending', query: 'pending' },
    { status: 'APPROVED', label: 'Approved', query: 'approved' },
    { status: 'REJECTED', label: 'Rejected', query: 'rejected' },
];

function tabIndexFromQuery(value: string | null): number {
    const index = TABS.findIndex(t => t.query === value);
    return index < 0 ? 0 : index;
}

type PendingAction = { kind: 'approve' | 'reject'; row: BetupFeedAdminPostDto } | null;

export default function BetupFeedPostsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = tabIndexFromQuery(searchParams.get('tab'));
    const status = TABS[tab].status;

    const [rows, setRows] = React.useState<BetupFeedAdminPostDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getBetupFeedPosts(status, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(loadErrorText(BETUP_FEED_LABELS.posts, 'Failed to load posts.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [status, paginationModel]);

    React.useEffect(() => { load(); }, [load]);

    const onTabChange = (_: React.SyntheticEvent, value: number) => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        setNotice(null);
        setError(null);
        setSearchParams({ tab: TABS[value].query }, { replace: true });
    };

    const openAction = (kind: 'approve' | 'reject', row: BetupFeedAdminPostDto) => setPending({ kind, row });

    const runAction = async (reason?: string, categories?: BetupFeedReviewCategory[]) => {
        if (pending == null) return;
        const { kind, row } = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        const postId = row.post.id;
        const result = kind === 'approve'
            ? await approveBetupFeedPost(postId)
            : await rejectBetupFeedPost(postId, reason ?? '', categories ?? []);
        setBusy(false);
        if (result.error != null) {
            setError(`Could not ${kind} post #${postId}: ${result.error}`);
            return;
        }
        setError(null);
        setNotice(kind === 'approve'
            ? `Post #${postId} approved and published at the top of the feed.`
            : `Post #${postId} rejected — the author sees the reason and can no longer edit it.`);
        await load();
    };

    const columns: GridColDef<BetupFeedAdminPostDto>[] = [
        {
            field: 'author', headerName: 'Author', width: 190, sortable: false,
            renderCell: ({ row }) => <FeedPlayer player={row.author} dense />,
        },
        {
            field: 'created_at', headerName: 'Created', width: 160, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography variant="body2">{formatFeedDate(row.post.created_at)}</Typography>
                    <Typography variant="caption" color="text.secondary">post #{row.post.id}</Typography>
                    {row.post.edited_at && (
                        <Typography variant="caption" color="text.secondary">edited {formatFeedDate(row.post.edited_at)}</Typography>
                    )}
                </Stack>
            ),
        },
        {
            field: 'text', headerName: 'Text', flex: 1, minWidth: 280, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={1} sx={{ width: '100%' }}>
                    <PostText text={row.post.text} />
                    <LinkedEntity match={row.post.match} team={row.post.team} />
                </Stack>
            ),
        },
        {
            field: 'image', headerName: 'Image', width: 116, sortable: false,
            renderCell: ({ row }) => <FeedImage url={row.post.image_url} expired={row.post.image_expired} />,
        },
        {
            field: 'review_status', headerName: 'Review', width: 230, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.75}>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        <ReviewStatusChip status={row.review_status} />
                        <ReviewedByChip reviewer={row.reviewed_by} />
                        {(row.open_reports_count ?? 0) > 0 && (
                            <Chip size="small" color="error" label={`${row.open_reports_count} open report${row.open_reports_count === 1 ? '' : 's'}`} />
                        )}
                        {row.hidden && <Chip size="small" variant="outlined" label="hidden" />}
                        {row.deleted && <Chip size="small" variant="outlined" color="error" label="deleted" />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">reviewed {formatFeedDate(row.reviewed_at)}</Typography>
                    <CategoryChips categories={row.review_categories} />
                    {row.review_reason && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.review_reason}</Typography>
                    )}
                </Stack>
            ),
        },
        {
            field: 'last_review', headerName: 'Last model review', width: 180, sortable: false,
            renderCell: ({ row }) => row.last_review == null
                ? <Typography variant="body2" color="text.secondary">—</Typography>
                : (
                    <Stack>
                        <Typography variant="body2">{row.last_review.model}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatCount(row.last_review.input_tokens)} in / {formatCount(row.last_review.output_tokens)} out
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{formatUsd(row.last_review.cost_usd, 6)}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatFeedDate(row.last_review.created_at)}</Typography>
                    </Stack>
                ),
        },
        {
            field: 'actions', headerName: 'Actions', width: 130, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={1}>
                    {row.review_status !== 'APPROVED' && (
                        <Button size="small" variant="contained" color="success" disabled={busy || row.deleted}
                            onClick={() => openAction('approve', row)}>
                            {row.review_status === 'REJECTED' ? 'Approve anyway' : 'Approve'}
                        </Button>
                    )}
                    {row.review_status !== 'REJECTED' && (
                        <Button size="small" variant="outlined" color="error" disabled={busy || row.deleted}
                            onClick={() => openAction('reject', row)}>Reject</Button>
                    )}
                </Stack>
            ),
        },
    ];

    const subject = pending == null ? '' : (
        <Tooltip title={pending.row.post.text}>
            <span>Post #{pending.row.post.id} by {pending.row.author?.name ?? 'unknown'}: “{shorten(pending.row.post.text)}”</span>
        </Tooltip>
    );

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">BETUP Feed — posts</Typography>
            <Typography variant="body2" color="text.secondary">
                Player posts by review status. Only approved posts are visible to other players; the author also sees
                their own pending and rejected posts. You can override Claude either way: approving a rejected post
                publishes it at the top of the feed; rejecting an approved one (for example after reports) takes it off
                the feed with your reason and categories, resolves its reports, and the author can no longer edit it.
            </Typography>
            <Tabs value={tab} onChange={onTabChange}>
                {TABS.map(t => <Tab key={t.status} label={t.label} />)}
            </Tabs>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => row.post.id}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 20, 50]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                sx={{ '& .MuiDataGrid-cell': { py: 1.5, alignItems: 'flex-start' } }}
                localeText={{ noRowsLabel: `No ${TABS[tab].label.toLowerCase()} posts` }}
            />
            <ConfirmDialog
                open={pending?.kind === 'approve'}
                title="Approve post?"
                confirmLabel={pending?.row.review_status === 'REJECTED' ? 'Approve anyway' : 'Approve'}
                confirmColor="success"
                onClose={() => setPending(null)}
                onConfirm={() => runAction()}
                text={pending == null ? '' : (
                    <>
                        {subject}
                        <br />
                        {pending.row.review_status === 'REJECTED'
                            ? `Overrides the rejection${pending.row.reviewed_by === 'MODEL' ? ' by Claude' : ''}: the post is published at the top of the feed (unless it is hidden) and the author sees it approved.`
                            : 'The post will be published at the top of the feed (unless it is hidden).'}
                    </>
                )}
            />
            <RejectPostDialog
                open={pending?.kind === 'reject'}
                subject={subject}
                initialCategories={pending?.row.review_categories as string[] | null | undefined}
                onClose={() => setPending(null)}
                onConfirm={(reason, categories) => runAction(reason, categories)}
            />
        </Stack>
    );
}
